import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Eye, Target, Heart } from 'lucide-react';
import SEO from '../components/SEO';
import api from '../utils/api';
import { resolveImage } from '../utils/imageUrl';

const ICON_MAP = [Eye, Target, Heart];

const DEFAULT_VALUES = [
  { title: 'Our Vision', desc: "To be the world's most trusted premium sample solutions provider, setting the standard for quality and innovation in the textile industry." },
  { title: 'Our Mission', desc: 'Delivering exceptional sample solutions that empower brands to showcase their collections with precision, elegance, and impact.' },
  { title: 'Our Values', desc: 'Quality, integrity, innovation, and customer-centricity are the pillars that guide every decision we make.' },
];

const DEFAULT_TIMELINE = [
  { year: '2004', title: 'Founded', desc: 'House of Sample was established with a vision to revolutionize textile sampling.' },
  { year: '2008', title: 'Global Expansion', desc: 'Expanded operations to serve clients across 20+ countries.' },
  { year: '2014', title: 'Innovation Hub', desc: 'Launched our state-of-the-art sample production facility.' },
  { year: '2020', title: 'Digital Transformation', desc: 'Introduced digital catalogues and online ordering systems.' },
  { year: '2024', title: 'Industry Leader', desc: 'Recognized as a leading premium sample solutions provider globally.' },
];

const DEFAULT_STATS = [
  { value: '20+', label: 'Years of Excellence' },
  { value: '500+', label: 'Products' },
  { value: '50+', label: 'Global Clients' },
];

export default function About() {
  const [heroImg, setHeroImg]     = useState(null);
  const [heroTitle, setHeroTitle] = useState('About Us');
  const [heroSub, setHeroSub]     = useState('Who We Are');
  const [heroObjPos, setHeroObjPos] = useState('50% 50%');
  const [storyImg, setStoryImg]   = useState(null);
  const [storyObjPos, setStoryObjPos] = useState('50% 50%');
  const [storyTitle, setStoryTitle] = useState('Crafting Excellence Since 2004');
  const [storyParagraphs, setStoryParagraphs] = useState([
    'House of Sample was born from a passion for precision and a commitment to quality. For over two decades, we have been the trusted partner for leading textile brands, fashion houses, and retailers worldwide.',
    'Our expertise spans sample catalogues, books, hangers, waterfalls, and swatches — each crafted with meticulous attention to detail and an unwavering dedication to excellence.',
  ]);
  const [storyExtra, setStoryExtra] = useState('');
  const [showMore, setShowMore]   = useState(false);
  const [values, setValues]       = useState(DEFAULT_VALUES);
  const [timeline, setTimeline]   = useState(DEFAULT_TIMELINE);
  const [stats, setStats]         = useState(DEFAULT_STATS);

  useEffect(() => {
    api.get('/content?page=about').then(res => {
      const items = res.data.content || [];
      const get = s => items.find(c => c.section === s);

      const h = get('hero');
      if (h) {
        if (h.image) setHeroImg(resolveImage(h.image));
        if (h.title) setHeroTitle(h.title);
        if (h.subtitle) setHeroSub(h.subtitle);
        if (h.extraData?.objectPosition) setHeroObjPos(h.extraData.objectPosition);
      }

      const st = get('story');
      if (st) {
        if (st.image) setStoryImg(resolveImage(st.image));
        if (st.extraData?.objectPosition) setStoryObjPos(st.extraData.objectPosition);
        if (st.title) setStoryTitle(st.title);

        // Prefer extraData.paragraphs (new format) — each paragraph stored independently
        if (st.extraData?.paragraphs?.length) {
          setStoryParagraphs(st.extraData.paragraphs.filter(Boolean));
          setStoryExtra(st.extraData.extra || '');
        } else if (st.body) {
          // Old format — split on double newline
          const parts = st.body.split('\n\n');
          const paras = parts.slice(0, 4).filter(Boolean);
          const extra = parts.slice(4).join('\n\n');
          if (paras.length) setStoryParagraphs(paras);
          setStoryExtra(extra);
        }
      }

      const v = get('values');
      if (v?.extraData?.items?.length) setValues(v.extraData.items);

      const tl = get('timeline');
      if (tl?.extraData?.items?.length) setTimeline(tl.extraData.items);

      const s = get('stats');
      if (s?.extraData?.items?.length) setStats(s.extraData.items);
    }).catch(() => {});
  }, []);

  return (
    <>
      <SEO title="About Us" description="Learn about House of Sample — our story, vision, and mission." />

      {/* Hero */}
      <section className="relative h-[432px] flex items-end pb-16 overflow-hidden">
        <img
          src={heroImg || 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80'}
          alt="About"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: heroObjPos }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <p className="text-gold text-xs tracking-[0.3em] uppercase mb-2">{heroSub}</p>
          <h1 className="font-serif text-4xl md:text-5xl text-white">{heroTitle}</h1>
        </div>
      </section>

      {/* Stats bar */}
      {stats.length > 0 && (
        <section className="bg-white border-b border-gray-100 py-8">
          <div className="max-w-7xl mx-auto px-6 flex flex-wrap justify-center gap-12">
            {stats.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="text-center">
                <p className="font-serif text-3xl text-gold">{s.value}</p>
                <p className="text-xs text-gray-500 tracking-widest uppercase mt-1">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Intro / Story */}
      <section className="py-24 px-6 max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-start">
        <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
          <p className="text-gold text-xs tracking-[0.3em] uppercase mb-4">Our Story</p>
          <h2 className="section-title text-left mb-6">{storyTitle}</h2>
          <div className="w-16 h-px bg-gold mb-8" />

          {/* Paragraphs — each visually separated */}
          <div className="space-y-5">
            {storyParagraphs.map((para, i) => (
              <motion.p
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="text-ink-600 leading-relaxed text-[15px] border-l-2 border-transparent hover:border-gold pl-4 transition-colors duration-300"
              >
                {para}
              </motion.p>
            ))}
          </div>

          {/* Read More — additional content */}
          {storyExtra && (
            <>
              {/* Expanded content */}
              {showMore && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.4, ease: 'easeInOut' }}
                  className="mt-5 space-y-5 overflow-hidden"
                >
                  {/* Split extra on double newline to preserve paragraph format */}
                  {storyExtra.split('\n\n').filter(p => p.trim()).map((para, i) => (
                    <motion.p
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.07 }}
                      className="text-ink-600 leading-relaxed text-[15px] border-l-2 border-gold pl-4"
                    >
                      {para.trim()}
                    </motion.p>
                  ))}
                </motion.div>
              )}

              {/* Toggle button */}
              <button
                onClick={() => setShowMore(v => !v)}
                className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-gold border border-gold px-5 py-2.5 hover:bg-gold hover:text-white transition-all duration-300 group"
              >
                {showMore ? 'Read Less' : 'Read More'}
                <span
                  className="inline-block transition-transform duration-300"
                  style={{ transform: showMore ? 'rotate(180deg)' : 'rotate(0deg)' }}
                >
                  ↓
                </span>
              </button>
            </>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
          className="relative sticky top-24">
          <img
            src={storyImg || 'https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=800&q=80'}
            alt="Our Story"
            className="w-full aspect-square object-cover border border-dark-600"
            style={{ objectPosition: storyObjPos }} />
          <div className="absolute -bottom-6 -left-6 w-32 h-32 border-2 border-gold" />
        </motion.div>
      </section>

      {/* Vision / Mission / Values */}
      <section className="py-24 bg-dark-800 border-y border-dark-600">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="section-title">Our Foundation</h2>
            <div className="gold-line" />
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {values.map((v, i) => {
              const Icon = ICON_MAP[i % ICON_MAP.length];
              return (
                <motion.div key={i}
                  initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.15 }}
                  className="text-center p-8 border border-dark-600 hover:border-gold transition-colors duration-300 bg-white">
                  <Icon className="text-gold mx-auto mb-4" size={32} />
                  <h3 className="font-serif text-xl text-ink mb-3">{v.title}</h3>
                  <p className="text-ink-400 text-sm leading-relaxed">{v.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 px-6 max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-gold text-xs tracking-[0.3em] uppercase mb-3">Our Journey</p>
          <h2 className="section-title">Milestones</h2>
          <div className="gold-line" />
        </div>
        <div className="relative">
          <div className="absolute left-1/2 -translate-x-px top-0 bottom-0 w-px bg-dark-600" />
          {timeline.map((item, i) => (
            <motion.div key={i}
              initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              className={`relative flex items-center mb-12 ${i % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
              <div className={`w-1/2 ${i % 2 === 0 ? 'pr-12 text-right' : 'pl-12 text-left'}`}>
                <span className="text-gold font-serif text-2xl">{item.year}</span>
                <h3 className="text-ink font-serif text-lg mt-1">{item.title}</h3>
                <p className="text-ink-400 text-sm mt-2">{item.desc}</p>
              </div>
              <div className="absolute left-1/2 -translate-x-1/2 w-4 h-4 bg-gold rounded-full border-2 border-white" />
              <div className="w-1/2" />
            </motion.div>
          ))}
        </div>
      </section>
    </>
  );
}
